import React, { useState, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTeamPortalAuth } from '../contexts/TeamPortalAuthContext'
import { 
  LayoutDashboard, Users, Calendar, MessageCircle, LogOut, Menu, X, 
  ChevronDown, Trophy, Bell, Settings, Home, Key, UserPlus,
  CreditCard, User, CalendarDays
} from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

export default function TeamPortalLayout() {
  const { user, token, logout } = useTeamPortalAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { teamId } = useParams()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [teams, setTeams] = useState([])
  const [currentTeam, setCurrentTeam] = useState(null)
  const [showTeamSelector, setShowTeamSelector] = useState(false)
  const [loadingTeams, setLoadingTeams] = useState(true)

  // Fetch user's teams
  useEffect(() => {
    if (token) {
      fetchTeams()
    }
  }, [token])

  // Set current team when teamId changes
  useEffect(() => {
    if (teamId && teams.length > 0) {
      const team = teams.find(t => t.team_id === teamId)
      setCurrentTeam(team)
    }
  }, [teamId, teams])

  const fetchTeams = async () => {
    try {
      const res = await fetch(`${API_URL}/api/teams`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })
      if (res.ok) {
        const data = await res.json()
        // data is an array of teams directly
        setTeams(data || [])
        
        // If no teamId in URL and we have teams, redirect to first team
        if (!teamId && data?.length > 0) {
          navigate(`/portal/team/${data[0].team_id}`)
        }
      }
    } catch (err) {
      console.error('Error fetching teams:', err)
    } finally {
      setLoadingTeams(false)
    }
  }

  // Get user's role in current team
  const getUserRole = () => {
    if (!currentTeam || !user) return null
    // Check team_members for role - this comes from the API response
    // The role can be in user_role, role, or my_role depending on the endpoint
    return currentTeam.user_role || currentTeam.my_role || currentTeam.role || 'member'
  }

  const userRole = getUserRole()
  const canManage = ['owner', 'admin', 'coach', 'manager', 'director', 'head_coach', 'team_manager'].includes(userRole)

  const navigation = [
    { name: 'Dashboard', href: '', icon: LayoutDashboard },
    { name: 'Schedule', href: 'schedule', icon: Calendar },
    { name: 'Calendar', href: 'calendar', icon: CalendarDays },
    { name: 'Roster', href: 'roster', icon: Users },
    { name: 'Messages', href: 'messages', icon: MessageCircle },
    { name: 'Chat', href: 'chat', icon: MessageCircle },
    { name: 'Invoices', href: 'invoices', icon: CreditCard },
    { name: 'Notifications', href: 'notifications', icon: Bell },
  ]

  // Admin-only navigation items
  const adminNavigation = canManage ? [
    { name: 'Join Requests', href: 'join-requests', icon: UserPlus },
    { name: 'Invite Codes', href: 'invite-codes', icon: Key },
    { name: 'Add Player', href: 'players/new', icon: UserPlus },
  ] : []

  // Account navigation (bottom)
  const accountNavigation = [
    { name: 'Profile', href: 'profile', icon: User },
  ]

  const NavItem = ({ item, mobile = false }) => {
    const basePath = `/portal/team/${teamId}`
    const itemPath = item.href ? `${basePath}/${item.href}` : basePath
    const isActive = location.pathname === itemPath || 
      (item.href === '' && location.pathname === basePath)
    
    return (
      <Link
        to={itemPath}
        onClick={mobile ? () => setSidebarOpen(false) : undefined}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
          isActive 
            ? 'bg-black text-white shadow-lg' 
            : 'text-zinc-300 hover:bg-zinc-800 hover:text-black'
        }`}
      >
        <item.icon className="w-5 h-5" />
        <span className="font-medium">{item.name}</span>
      </Link>
    )
  }

  if (loadingTeams) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    )
  }

  if (teams.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-900 px-4">
        <div className="text-center max-w-md">
          <Trophy className="w-16 h-16 text-zinc-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">No Teams Found</h2>
          <p className="text-zinc-400 mb-6">
            You're not a member of any teams yet. Check your email for team invitations, enter an invite code, or contact your coach.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/portal/join')}
              className="w-full px-6 py-3 bg-black text-white rounded-xl font-medium hover:bg-zinc-700 transition-colors"
              data-testid="join-team-button"
            >
              Enter Invite Code
            </button>
            <button
              onClick={() => navigate('/portal/create-team')}
              className="w-full px-6 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-colors"
            >
              Create a Team
            </button>
            <button
              onClick={logout}
              className="w-full px-6 py-3 border border-zinc-700 text-zinc-200 rounded-xl font-medium hover:bg-zinc-900 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 w-72 bg-zinc-800 shadow-2xl">
          <div className="flex items-center justify-between p-5 border-b border-zinc-700">
            <img 
              src="/squadline-logo.png" 
              alt="Squadline" 
              className="h-8 w-auto"
            />
            <button 
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-full hover:bg-zinc-800 transition-colors"
            >
              <X className="w-5 h-5 text-zinc-300" />
            </button>
          </div>
          
          {/* Team Selector - Mobile */}
          <div className="p-4 border-b border-zinc-700">
            <button
              onClick={() => setShowTeamSelector(!showTeamSelector)}
              className="w-full flex items-center justify-between p-3 bg-zinc-900 rounded-xl hover:bg-zinc-800 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-white text-sm truncate max-w-[140px]">
                    {currentTeam?.name || 'Select Team'}
                  </p>
                  <p className="text-xs text-zinc-400 capitalize">{userRole || 'Member'}</p>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${showTeamSelector ? 'rotate-180' : ''}`} />
            </button>
            
            {showTeamSelector && (
              <div className="mt-2 space-y-1">
                {teams.map(team => (
                  <button
                    key={team.team_id}
                    onClick={() => {
                      navigate(`/portal/team/${team.team_id}`)
                      setShowTeamSelector(false)
                      setSidebarOpen(false)
                    }}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                      team.team_id === teamId ? 'bg-zinc-800' : 'hover:bg-zinc-900'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-zinc-700 flex items-center justify-center">
                      <Trophy className="w-4 h-4 text-zinc-300" />
                    </div>
                    <span className="text-sm font-medium text-zinc-200 truncate">{team.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <nav className="p-4 space-y-1">
            {navigation.map((item) => (
              <NavItem key={item.name} item={item} mobile />
            ))}
            {adminNavigation.length > 0 && (
              <>
                <div className="pt-3 mt-3 border-t border-zinc-700">
                  <p className="px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Admin</p>
                </div>
                {adminNavigation.map((item) => (
                  <NavItem key={item.name} item={item} mobile />
                ))}
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-1 bg-zinc-800 border-r border-zinc-700">
          {/* Logo */}
          <div className="flex items-center h-20 px-6 border-b border-zinc-700">
            <img 
              src="/squadline-logo.png" 
              alt="Squadline" 
              className="h-8 w-auto"
            />
          </div>
          
          {/* Team Selector */}
          <div className="p-4 border-b border-zinc-700">
            <button
              onClick={() => setShowTeamSelector(!showTeamSelector)}
              className="w-full flex items-center justify-between p-3 bg-zinc-900 rounded-xl hover:bg-zinc-800 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center overflow-hidden">
                  {currentTeam?.logo ? (
                    <img src={currentTeam.logo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Trophy className="w-5 h-5 text-white" />
                  )}
                </div>
                <div className="text-left">
                  <p className="font-semibold text-white text-sm truncate max-w-[140px]">
                    {currentTeam?.name || 'Select Team'}
                  </p>
                  <p className="text-xs text-zinc-400 capitalize">{userRole || 'Member'}</p>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${showTeamSelector ? 'rotate-180' : ''}`} />
            </button>
            
            {showTeamSelector && (
              <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {teams.map(team => (
                  <button
                    key={team.team_id}
                    onClick={() => {
                      navigate(`/portal/team/${team.team_id}`)
                      setShowTeamSelector(false)
                    }}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                      team.team_id === teamId ? 'bg-zinc-800' : 'hover:bg-zinc-900'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-zinc-700 flex items-center justify-center overflow-hidden">
                      {team.logo ? (
                        <img src={team.logo} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Trophy className="w-4 h-4 text-zinc-300" />
                      )}
                    </div>
                    <span className="text-sm font-medium text-zinc-200 truncate">{team.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
            {navigation.map((item) => (
              <NavItem key={item.name} item={item} />
            ))}
            {adminNavigation.length > 0 && (
              <>
                <div className="pt-4 mt-4 border-t border-zinc-700">
                  <p className="px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Admin</p>
                </div>
                {adminNavigation.map((item) => (
                  <NavItem key={item.name} item={item} />
                ))}
              </>
            )}
          </nav>
          
          {/* Account nav */}
          <div className="px-4 pb-2 pt-2 border-t border-zinc-700">
            <p className="px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Account</p>
            {accountNavigation.map((item) => (
              <NavItem key={item.name} item={item} />
            ))}
          </div>

          {/* User section */}
          <div className="p-4 border-t border-zinc-700">
            <div className="flex items-center gap-3 p-3 mb-3 bg-zinc-900 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0 overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-semibold text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm text-white truncate">{user?.name}</p>
                <p className="text-xs text-zinc-400 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 w-full px-4 py-3 text-sm font-medium text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Mobile header */}
        <div className="sticky top-0 z-40 flex items-center justify-between h-16 px-4 bg-zinc-800 border-b border-zinc-700 lg:hidden">
          <div className="flex items-center">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
            >
              <Menu className="w-6 h-6 text-zinc-200" />
            </button>
            <span className="ml-3 font-semibold text-white truncate max-w-[180px]">
              {currentTeam?.name || 'Team Portal'}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-zinc-800 transition-colors">
              <Bell className="w-5 h-5 text-zinc-300" />
            </button>
          </div>
        </div>
        
        {/* Page content */}
        <main className="p-6 lg:p-8">
          <Outlet context={{ team: currentTeam, userRole, canManage, token }} />
        </main>
      </div>
    </div>
  )
}
